import React from 'react';
import { CheckCircle, Clock, Sparkles, Eye, PackageCheck } from 'lucide-react';

/**
 * OrderProgressTracker
 * Premium 4-stage tracker for customer orders.
 * Stages: 0 Order Received → 1 In Production → 2 Final Review → 3 Ready
 * Matches Visual Creationz black/gold design language.
 */

const STAGES = [
  { key: 'received', label: 'Order Received', icon: PackageCheck, blurb: 'Deposit confirmed - your project date is secured.' },
  { key: 'production', label: 'In Production', icon: Sparkles, blurb: 'Your cinematic presentation is being crafted.' },
  { key: 'review', label: 'Final Review', icon: Eye, blurb: 'Quality checks and revisions being finalised.' },
  { key: 'ready', label: 'Ready', icon: CheckCircle, blurb: 'Your presentation is ready to download.' },
];

const OrderProgressTracker = ({ stage = 0, className = '' }) => {
  const currentStage = Math.max(0, Math.min(3, Number(stage) || 0));
  const activeStageInfo = STAGES[currentStage];

  return (
    <div
      className={`w-full ${className}`}
      data-testid="order-progress-tracker"
      data-stage={currentStage}
    >
      {/* Section title */}
      <div className="mb-6 text-center md:text-left">
        <p className="uppercase tracking-[0.3em] text-[10px] md:text-xs text-amber-400 mb-2">Project Status</p>
        <h3 className="text-xl md:text-2xl font-semibold text-white">
          {activeStageInfo.label}
        </h3>
        <p className="text-sm text-gray-400 mt-1">{activeStageInfo.blurb}</p>
      </div>

      {/* Desktop: horizontal tracker with connecting line */}
      <div className="hidden md:block">
        <div className="relative">
          {/* Base connecting line */}
          <div className="absolute top-6 left-6 right-6 h-[2px] bg-white/10" aria-hidden="true" />
          {/* Filled progress line */}
          <div
            className="absolute top-6 left-6 h-[2px] bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-700 ease-out"
            style={{ width: `calc((100% - 3rem) * ${currentStage / (STAGES.length - 1)})` }}
            aria-hidden="true"
          />

          <div className="relative grid grid-cols-4 gap-4">
            {STAGES.map((s, idx) => {
              const Icon = s.icon;
              const isComplete = idx < currentStage;
              const isActive = idx === currentStage;
              return (
                <div key={s.key} className="flex flex-col items-center text-center" data-testid={`tracker-step-${s.key}`}>
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      isComplete
                        ? 'bg-amber-500 border-amber-500 text-black'
                        : isActive
                        ? 'bg-amber-500/15 border-amber-500 text-amber-400 shadow-[0_0_24px_rgba(245,158,11,0.35)]'
                        : 'bg-black border-white/15 text-gray-600'
                    }`}
                  >
                    {isComplete ? <CheckCircle size={20} strokeWidth={2.5} /> : <Icon size={20} strokeWidth={2} />}
                  </div>
                  <div className="mt-3">
                    <div
                      className={`text-sm font-medium ${
                        isComplete || isActive ? 'text-white' : 'text-gray-500'
                      }`}
                    >
                      {s.label}
                    </div>
                    {isActive && (
                      <div className="flex items-center justify-center gap-1.5 mt-1 text-[11px] text-amber-400 uppercase tracking-widest font-semibold">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-400"></span>
                        </span>
                        Current
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile: vertical tracker */}
      <div className="md:hidden space-y-4">
        {STAGES.map((s, idx) => {
          const Icon = s.icon;
          const isComplete = idx < currentStage;
          const isActive = idx === currentStage;
          const isLast = idx === STAGES.length - 1;
          return (
            <div key={s.key} className="relative flex items-start gap-4" data-testid={`tracker-step-mobile-${s.key}`}>
              {/* Vertical connector */}
              {!isLast && (
                <div
                  className={`absolute left-[19px] top-10 w-[2px] h-[calc(100%+8px)] ${
                    idx < currentStage ? 'bg-gradient-to-b from-amber-500 to-amber-400' : 'bg-white/10'
                  }`}
                  aria-hidden="true"
                />
              )}
              <div
                className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-all duration-300 ${
                  isComplete
                    ? 'bg-amber-500 border-amber-500 text-black'
                    : isActive
                    ? 'bg-amber-500/15 border-amber-500 text-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.3)]'
                    : 'bg-black border-white/15 text-gray-600'
                }`}
              >
                {isComplete ? <CheckCircle size={16} strokeWidth={2.5} /> : <Icon size={16} strokeWidth={2} />}
              </div>
              <div className="flex-1 pt-1">
                <div className={`text-sm font-medium ${isComplete || isActive ? 'text-white' : 'text-gray-500'}`}>
                  {s.label}
                </div>
                {isActive && (
                  <div className="text-xs text-amber-400 mt-0.5">In progress</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderProgressTracker;
